import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiConfiguration } from '@/core/api/api-configuration';
import { BaseService } from '@/core/api/base-service';
import { environment } from '@env/environment';
import { SICOM_PATHS } from 'src/sicom-paths';
import { Page } from '@/shared/models/page.model';
import { MedicoPendenciaResponse } from '../pendencia/medico-pendencia.model';
import { MedicoAtivoTotalUfResponse } from './medico-ativo-total-uf.model';
import { MedicoEstatisticaAgrupadaResponse } from './medico-estatistica-agrupada.model';
import { MedicoCadastroGeralResponse } from './medico-cadastro-geral.model';
import { MedicoSituacaoEnderecoTipoResponse } from './medico-situacao-endereco-tipo.model';

@Injectable({ providedIn: 'root' })
export class MedicoControleAdministrativoService extends BaseService {
    constructor(config: ApiConfiguration, http: HttpClient) {
        super(config, http, environment.apis.apiSicom);
    }

    readonly PATH = SICOM_PATHS.registro.medico.controleAdministrativo;

    consultarTotalInscricaoPrincipal(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/inscricao-principal`, this.criarOpcoes(uf, context));
    }

    consultarTotalInscricaoSecundaria(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/inscricao-secundaria`, this.criarOpcoes(uf, context));
    }

    consultarTotalInscricaoProvisoria(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/inscricao-provisoria`, this.criarOpcoes(uf, context));
    }

    consultarTotalTransferenciasConcedidas(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/transferencias-concedidas`, this.criarOpcoes(uf, context));
    }

    consultarTotalBaixasExercicioProfissional(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/baixas-exercicio-profissional`, this.criarOpcoes(uf, context));
    }

    consultarTotalCancelamentosInscricao(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/cancelamentos-inscricao`, this.criarOpcoes(uf, context));
    }

    consultarTotalTitulosEspecialistaEmitidos(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/titulos-especialista-emitidos`, this.criarOpcoes(uf, context));
    }

    consultarUltimoCrmCadastrado(uf?: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/ultimo-crm-cadastrado`, this.criarOpcoes(uf, context));
    }

    consultarTotalMedicosAtivosPorUf(context?: HttpContext): Observable<MedicoAtivoTotalUfResponse[]> {
        return this.http.get<MedicoAtivoTotalUfResponse[]>(`${this.PATH}/consulta/medicos-ativos-por-uf`, { context });
    }

    consultarNovasInscricoesPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/novas-inscricoes-por-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarDistribuicaoFaixaEtaria(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/faixa-etaria`, this.criarOpcoes(uf, context));
    }

    consultarDistribuicaoGenero(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/genero`, this.criarOpcoes(uf, context));
    }

    consultarMedicosPorMunicipio(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/municipios`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarFormadosExteriorPorPais(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/formados-exterior-pais`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarEspecialidadesMaisRegistradas(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/especialidades`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarTempoMedioFormaturaPrimeiraInscricao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-medio-formatura-primeira-inscricao`, this.criarOpcoes(uf, context));
    }

    consultarConversaoInscricaoProvisoria(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/conversao-inscricao-provisoria`, this.criarOpcoes(uf, context));
    }

    consultarTransferenciasOrigemDestino(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/transferencias-origem-destino`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarTempoMedioTramitacaoTransferencia(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-medio-tramitacao-transferencia`, this.criarOpcoes(uf, context));
    }

    consultarMovimentacaoSituacaoCadastral(uf?: string, ano?: number, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/movimentacao-situacao-cadastral`, this.criarOpcoes(uf, context, { ano, limite }));
    }

    consultarTempoMedioSituacaoCadastral(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-medio-situacao-cadastral`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarDocumentosAusentes(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/documentos-ausentes`, this.criarOpcoes(uf, context));
    }

    consultarCadastrosPorAtualizacao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cadastros-por-atualizacao`, this.criarOpcoes(uf, context));
    }

    consultarContatosValidos(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/contatos-validos`, this.criarOpcoes(uf, context));
    }

    consultarDuplicidadesPotenciais(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/duplicidades-potenciais`, this.criarOpcoes(uf, context));
    }

    consultarRankingFaculdadesFormacao(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/faculdades-formacao`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarMedicosSemEspecialidade(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/medicos-sem-especialidade`, this.criarOpcoes(uf, context));
    }

    consultarMediaEspecialidadesPorMedico(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/media-especialidades-por-medico`, this.criarOpcoes(uf, context));
    }

    consultarSubgruposEspeciais(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/subgrupos-especiais`, this.criarOpcoes(uf, context));
    }

    consultarCancelamentosPorSituacao(uf?: string, ano?: number, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cancelamentos-por-situacao`, this.criarOpcoes(uf, context, { ano, limite }));
    }

    consultarReinscricoesAposCancelamento(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/reinscricoes-apos-cancelamento`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarProtocolosMedicoPorTipo(uf?: string, ano?: number, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/protocolos-medico-por-tipo`, this.criarOpcoes(uf, context, { ano, limite }));
    }

    consultarEmissoesDocumentais(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/emissoes-documentais`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarCadastrosAtualizadosPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/cadastros-atualizados-por-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarAnotacoesAdministrativas(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/anotacoes-administrativas`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarMedicosEstatisticaCadastro(estatisticaId: string, uf?: string | null, ano?: number, page?: number, size?: number, codigo?: string | null, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/estatisticas-cadastro/${estatisticaId}/medicos`, this.criarOpcoes(uf ?? undefined, context, { ano, page, size, codigo }));
    }

    consultarDistribuicaoPorSituacao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao`, this.criarOpcoes(uf, context));
    }

    consultarTelemedicinaTipos(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/telemedicina-tipos`, this.criarOpcoes(uf, context));
    }

    consultarDeficienciaTipos(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/deficiencia-tipos`, this.criarOpcoes(uf, context));
    }

    consultarPosGraduacaoTipos(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pos-graduacao-tipos`, this.criarOpcoes(uf, context));
    }

    consultarPosGraduacaoInstituicoes(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pos-graduacao-instituicoes`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarConflitoInteresseSituacao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/conflito-interesse-situacao`, this.criarOpcoes(uf, context));
    }

    consultarPcmsoRiscos(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/pcmso-riscos`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarVistosVencimentoFaixa(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/vistos-vencimento-faixa`, this.criarOpcoes(uf, context));
    }

    consultarProjecaoAposentadoria(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/projecao-aposentadoria`, this.criarOpcoes(uf, context));
    }

    consultarRetencaoFormados(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/retencao-formados`, this.criarOpcoes(uf, context));
    }

    consultarIdadeMediaEspecialidade(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/idade-media-especialidade`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarTempoInscricaoTitulo(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-inscricao-titulo`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarEvolucaoAnualAtivos(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/evolucao-anual-ativos`, this.criarOpcoes(uf, context));
    }

    consultarInteriorizacaoAnual(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/interiorizacao-anual`, this.criarOpcoes(uf, context));
    }

    consultarTempoMedioAtendimentoProtocolo(uf?: string, ano?: number, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tempo-medio-atendimento-protocolo`, this.criarOpcoes(uf, context, { ano, limite }));
    }

    consultarSituacaoMensalSituacao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao-mensal/situacao`, this.criarOpcoes(uf, context));
    }

    consultarSituacaoMensalSexo(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao-mensal/sexo`, this.criarOpcoes(uf, context));
    }

    consultarSituacaoMensalMovimento(uf?: string, mes?: number, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao-mensal/movimento`, this.criarOpcoes(uf, context, { mes, ano }));
    }

    consultarSituacaoMensalCancelamentos(uf?: string, mes?: number, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao-mensal/cancelamentos`, this.criarOpcoes(uf, context, { mes, ano }));
    }

    consultarSituacaoMensalEnderecoTipo(uf?: string, context?: HttpContext): Observable<MedicoSituacaoEnderecoTipoResponse[]> {
        return this.http.get<MedicoSituacaoEnderecoTipoResponse[]>(`${this.PATH}/consulta/situacao-mensal/endereco-tipo`, this.criarOpcoes(uf, context));
    }

    consultarSituacaoPorMes(uf?: string, ano?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/situacao-por-mes`, this.criarOpcoes(uf, context, { ano }));
    }

    consultarSituacaoMensalMedicos(cardId: string, uf?: string | null, mes?: number, ano?: number, page?: number, size?: number, codigo?: string | null, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/situacao-mensal/${cardId}/medicos`, this.criarOpcoes(uf ?? undefined, context, { mes, ano, page, size, codigo }));
    }

    consultarTotalMedicosAtivosNaData(uf: string | undefined, data: string, context?: HttpContext): Observable<number> {
        return this.http.get<number>(`${this.PATH}/consulta/medicos-ativos-data`, this.criarOpcoes(uf, context, { data }));
    }

    consultarMedicosAtivosNaData(uf: string | undefined, data: string, page?: number, size?: number, context?: HttpContext): Observable<Page<MedicoPendenciaResponse>> {
        return this.http.get<Page<MedicoPendenciaResponse>>(`${this.PATH}/consulta/medicos-ativos-data/medicos`, this.criarOpcoes(uf, context, { data, page, size }));
    }

    consultarCadastroGeral(inscricao?: string, genero?: string, context?: HttpContext): Observable<MedicoCadastroGeralResponse[]> {
        return this.http.get<MedicoCadastroGeralResponse[]>(`${this.PATH}/consulta/cadastro-geral`, this.criarOpcoes(undefined, context, { inscricao, genero }));
    }

    consultarIsencoesPorMotivo(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/isencoes-motivo`, this.criarOpcoes(uf, context));
    }

    consultarTermoLgpd(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/termo-lgpd`, this.criarOpcoes(uf, context));
    }

    consultarVistosConcedidosPorTipo(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/vistos-concedidos-tipo`, this.criarOpcoes(uf, context));
    }

    consultarOcorrenciasPorTipo(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/ocorrencias-tipo`, this.criarOpcoes(uf, context, { limite }));
    }

    consultarDiplomasApresentacao(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/diplomas-apresentacao`, this.criarOpcoes(uf, context));
    }

    consultarEstadoCivil(uf?: string, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/estado-civil`, this.criarOpcoes(uf, context));
    }

    consultarTipoSanguineo(uf?: string, limite?: number, context?: HttpContext): Observable<MedicoEstatisticaAgrupadaResponse[]> {
        return this.http.get<MedicoEstatisticaAgrupadaResponse[]>(`${this.PATH}/consulta/tipo-sanguineo`, this.criarOpcoes(uf, context, { limite }));
    }

    private criarOpcoes(uf?: string, context?: HttpContext, parametrosExtras: Record<string, string | number | null | undefined> = {}): { params?: HttpParams; context?: HttpContext } {
        let params = new HttpParams();

        if (uf) {
            params = params.set('uf', uf);
        }

        Object.entries(parametrosExtras).forEach(([chave, valor]) => {
            if (valor !== null && valor !== undefined && valor !== '') {
                params = params.set(chave, String(valor));
            }
        });

        if (!params.keys().length) {
            return { context };
        }

        return { params, context };
    }
}
